import Bowser from "bowser";
import { SwPopup } from "./sw-footer/sw-popup/sw-popup";

declare global {
    interface String {
        capitalize(): string;
        titleCase(): string;
    }

    interface Number {
        toKhmer(language: string): string;
    }

    interface Location {
        setHash(book: number, page: number): void;
    }

    interface Date {
        formatDate(): string;
    }

    interface Window {
        openPage(src: string): void;
    }
}

String.prototype.capitalize = function (): string {
    if (!this) return "";
    return this.charAt(0).toUpperCase() + this.slice(1);
};

String.prototype.titleCase = function (): string {
    return this.toLowerCase()
        .split(" ")
        .map((word) => word.capitalize())
        .join(" ");
};

Number.prototype.toKhmer = function (language: string): string {
    if (language === "khmer") {
        const khmerDigits = ["០", "១", "២", "៣", "៤", "៥", "៦", "៧", "៨", "៩"];

        return this.toString()
            .split("")
            .map((digit) => {
                const digitNumber = parseInt(digit, 10);
                if (isNaN(digitNumber)) {
                    throw new Error("Input must be a valid number");
                }
                return khmerDigits[digitNumber];
            })
            .join("");
    } else {
        return this.toString();
    }
};

Location.prototype.setHash = function (book: number, page: number): void {
    this.hash = `/book/${book}/page/${page}`; // does not push to history: maybe TODO: later
};

function replaceMonthWithKhmer(date: string) {
    const monthMap = {
        January: "មករា",
        February: "កុម្ភៈ",
        March: "មីនា",
        April: "មេសា",
        May: "ឧសភា",
        June: "មិថុនា",
        July: "កក្កដា",
        August: "សីហា",
        September: "កញ្ញា",
        October: "តុលា",
        November: "វិច្ឆិកា",
        December: "ធ្នូ"
    };

    for (const [en, kh] of Object.entries(monthMap)) {
        // Replace only the month (word boundary to avoid partial matches)
        const regex = new RegExp(`\\b${en}\\b`, "g");
        date = date.replace(regex, kh);
    }

    return date;
}

Date.prototype.formatDate = function (): string {
    const languages =
        LANGUAGE === "khmer" ? ["km-KH", "en-US"] : ["en-US", "km-KH"];

    const options: Intl.DateTimeFormatOptions = {
        month: "long",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
        timeZone:
            localStorage.getItem("timezone") ||
            (LANGUAGE === "khmer" ? "Asia/Phnom_Penh" : "America/Los_Angeles"),
        timeZoneName: "shortGeneric"
    };

    const formatted = this.toLocaleString(languages, options);

    if (LANGUAGE === "khmer") {
        return replaceMonthWithKhmer(
            formatted
                .replace("នៅ​", "វេលា")
                .replace("at", "នៅម៉ោង")
                .replace("AM", "ព្រឹក")
                .replace("PM", "យប់")
                .replace("Cambodia Time", "")
        );
    }

    return formatted;
};

export const isProduction = process.env.NODE_ENV === "production";

export const DATE = process.env.PARCEL_COMMIT_TIMESTAMP
    ? new Date(parseInt(process.env.PARCEL_COMMIT_TIMESTAMP) * 1000)
    : new Date();

// export function formatDate(date: Date) {
//     const languages =
//         LANGUAGE === "khmer" ? ["km-KH", "en-US"] : ["en-US", "km-KH"];

//     const options: Intl.DateTimeFormatOptions = {
//         month: "long",
//         day: "numeric",
//         year: "numeric",
//         hour: "numeric",
//         minute: "2-digit",
//         hour12: true,
//         timeZone:
//             LANGUAGE === "khmer" ? "Asia/Phnom_Penh" : "America/Los_Angeles",
//         timeZoneName: "shortGeneric"
//     };

//     return LANGUAGE === "khmer"
//         ? replaceMonthWithKhmer(
//               date
//                   .toLocaleString(languages, options)
//                   .replace("នៅ​", "វេលា")
//                   .replace("at", "នៅម៉ោង")
//                   .replace("AM", "ព្រឹក")
//                   .replace("PM", "យប់")
//                   .replace("Cambodia Time", "")
//           )
//         : date.toLocaleString(languages, options);
// }

export const HB_VAULT = isProduction
    ? "https://vault.333.eco"
    : "http://localhost:1234";

export const HB_ORG = isProduction
    ? "https://heartbank.org" // thank.hb.org
    : "http://localhost:61282";

export const SW_COM = isProduction
    ? "https://bedok.siliconwat.com"
    : "http://localhost:8787";

export const SW_AI = isProduction
    ? "https://us-central1-siliconwat.cloudfunctions.net"
    : "http://127.0.0.1:5004/siliconwat/us-central1";

export const LANGUAGE = localStorage.getItem("language") ?? "khmer"; // can use ||
export const THEME = "orange"; // deprecated #ffa500 TODO: get from :root via computed style; see map app // move to tailwind css class???
export const MAP = "leaderboard";

export function html(strings: TemplateStringsArray, ...values: any[]): string {
    // Combine the strings and interpolated values into a single string
    return strings.reduce(
        (result, str, i) => result + str + (values[i] || ""),
        ""
    );
}

const browser = Bowser.getParser(window.navigator.userAgent);
const platform = browser.getPlatform();
export const deviceName =
    platform.vendor && platform.model
        ? `${platform.vendor} ${platform.model}`
        : platform.model || platform.vendor || "Unknown Device";
export const deviceType = platform.type?.capitalize() || "Unknown Type"; // desktop or mobile or tablet
export const osName = browser.getOSName() || "Unknown OS";
export const osVersion = browser.getOSVersion() || "Unknown Version";
export const browserName = browser.getBrowserName() || "Unknown Browser";
export const browserVersion = browser.getBrowserVersion() || "Unknown Version";
export const isApple = ["iOS", "macOS", "watchOS", "tvOS"].includes(osName);
export const isSafari = browserName === "Safari";
export const isMobile = deviceType === "Mobile";
export const isDesktop = deviceType === "Desktop";

const isStandalone = window.matchMedia("(display-mode: standalone)").matches;
const isIOSStandalone = (navigator as any).standalone === true; // iOS-specific
export const isPwa = isStandalone || isIOSStandalone;

// TODO: don't cache ip bc user can change location ?? in memory might be ok // service worker caches already
// must be on client bc don't want server ip
let IP = "";
export async function getIP() {
    try {
        if (!IP) {
            const res = await fetch("https://api.ipify.org?format=json");
            const { ip } = await res.json();
            IP = ip;
        }
    } catch (error) {
        console.error(error);
    } finally {
        //console.log(IP);
        return IP;
    }
}

// TODO: move to server?
let location: any = {};
export async function getLocation(ip: string) {
    try {
        if (location.ip !== ip) {
            const geoRes = await fetch(`https://ipapi.co/${ip}/json/`);
            location = await geoRes.json();
        }
    } catch (error) {
        console.error(error);
    } finally {
        location.ip = ip;
        //console.log(location);
        localStorage.setItem("timezone", location.timezone || ""); //TODO: later get user's selected timezone instead
        return location;
    }
}

export async function pickFile(options?: {
    accept?: string;
    capture?: "microphone" | "camera" | "camcorder";
}): Promise<File> {
    const files = options ? await pickFiles(options) : await pickFiles();
    return files[0];
}

export async function pickFiles(options?: {
    accept?: string;
    capture?: "microphone" | "camera" | "camcorder";
    multiple?: boolean;
}): Promise<File[]> {
    return new Promise((resolve, reject) => {
        const input = document.createElement("input");
        input.type = "file";
        input.accept = options?.accept || "image/*";
        input.style.display = "none"; // Hide the input
        if (options?.capture) input.setAttribute("capture", options.capture);
        if (options?.multiple) input.multiple = true;
        document.body.appendChild(input);

        const cleanup = () => {
            clearTimeout(timeoutId);
            window.removeEventListener("focus", onFocus);
            document.body.removeChild(input);
        };

        input.addEventListener("change", () => {
            cleanup();
            if (input.files && input.files.length > 0) {
                resolve(Array.from(input.files));
            } else {
                reject(new Error("No file selected"));
            }
        });

        const onFocus = () => {
            setTimeout(() => {
                if (!input.files || input.files.length === 0) {
                    cleanup();
                    reject(new Error("cancelled"));
                }
            }, 500);
        };

        window.addEventListener("focus", onFocus);

        const timeoutId = setTimeout(() => {
            cleanup();
            reject(new Error("File selection timed out"));
        }, 60000); // 60 seconds

        input.click();
    });
}

// not used bc most websites deny iframe
window.openPage = function (src: string) {
    if (isPwa) {
        window.open(src, "_blank");
    } else {
        const popup = document.querySelector("sw-popup") as SwPopup | null;
        popup?.show(src);
    }
};
